import { Delete, NavigateBefore, NavigateNext } from "@mui/icons-material";
import { Box, Button, CircularProgress, Divider, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { format, getYear } from "date-fns";
import { useSnackbar } from "notistack";
import React from "react";
import { ApiError } from "../../Api/ApiError";
import { delStoreWorkingParticularSetting, getStoreWorkingParticularSettingList, getStoreWorkingPatternList, getStoreWorkingTimeList } from "../../Api/Store";
import { WorkingParticularSetting } from "../../Objects/WorkingParticularSetting";
import { WorkPattern } from "../../Objects/WorkPattern";
import { WorkPatternSet } from "../../Objects/WorkPatternSet";
import { StoreObjectStore } from "../../ObjectStore/StoreObjectStore";
import CategoryTitle from "../atoms/CategoryTitle";
import StoreWorkingTimeTable from "../molecules/StoreWorkingTimeTable";
import WorkingParticularSettingDialog from "./WorkingParticularSettingDialog";

const StoreDetailBusinessHours: React.FC = () => {
    let { enqueueSnackbar } = useSnackbar()

    const { storeObjectState } = React.useContext(StoreObjectStore)

    const [workPatternSetList, setWorkPatternSetList] = React.useState<WorkPatternSet[]>([])
    const [selectedWorkPatternSet, setSelectedWorkPatternSet] = React.useState<WorkPatternSet>()
    const [workingTimeList, setWorkingTimeList] = React.useState<WorkPattern[]>()

    const [workingParticularSettingList, setWorkingParticularSettingList] = React.useState<WorkingParticularSetting[]>()
    const [showWrkPtcYear, setShowWrkPtcYear] = React.useState<number>(getYear(new Date()))
    const [loadingWrkPtc, setLoadingWrkPtc] = React.useState<boolean>(false)

    const [showWorkingParticularSettingDialog, setShowWorkingParticularSettingDialog] = React.useState<boolean>(false)

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const loadStoreWorkingTimeList = React.useCallback((wrkptnset_id: number) => {
        getStoreWorkingTimeList({
            wrkptnset_id: wrkptnset_id
        }, {signal: abortController.signal}).then(result => {
            setWorkingTimeList(result.workingTimeList)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [abortController, enqueueSnackbar])

    const loadStoreWorkingParticularSettingList = React.useCallback((str_id: number, year: number) => {
        setLoadingWrkPtc(true)
        getStoreWorkingParticularSettingList({
            str_id: str_id,
            year: year
        }, {signal: abortController.signal}).then(result => {
            setWorkingParticularSettingList(result.workingParticularSettingList)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        }).finally(() => {
            setLoadingWrkPtc(false)
        })
    }, [abortController, enqueueSnackbar])

    const doDelete = React.useCallback((wrkptc_id: number) => {
        delStoreWorkingParticularSetting({
            wrkptc_id: wrkptc_id
        }, {signal: abortController.signal}).then(() => {
            enqueueSnackbar("削除しました", { variant: 'success' })
            if( storeObjectState.storeData?.str_id ) {
                loadStoreWorkingParticularSettingList(storeObjectState.storeData?.str_id, showWrkPtcYear);
            }
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [abortController, enqueueSnackbar, storeObjectState.storeData?.str_id, loadStoreWorkingParticularSettingList, showWrkPtcYear])

    React.useEffect(() => {
        const controller = new AbortController()
        if (storeObjectState.storeData?.str_id) {
            getStoreWorkingPatternList({
                str_id: storeObjectState.storeData?.str_id
            }, {signal: controller.signal}).then(result => {
                setWorkPatternSetList(result.workPatternSetList)
                setSelectedWorkPatternSet(result.workPatternSetList[result.workPatternSetList.length - 1])
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
        return () => {
            controller.abort()
        }
    }, [storeObjectState.storeData?.str_id, enqueueSnackbar])

    React.useEffect(() => {
        if (storeObjectState.storeData?.str_id) {
            loadStoreWorkingParticularSettingList(storeObjectState.storeData?.str_id, showWrkPtcYear)
        }
    }, [storeObjectState.storeData?.str_id, showWrkPtcYear, loadStoreWorkingParticularSettingList])

    React.useEffect(() => {
        if (selectedWorkPatternSet) {
            loadStoreWorkingTimeList(selectedWorkPatternSet.wrkptnset_id)
        }
    }, [selectedWorkPatternSet, loadStoreWorkingTimeList])

    return (
        <Paper sx={{ mt: 2, pb: 2 }}>
            <CategoryTitle
                text="営業時間設定"
            />
            <Stack
                direction="row"
                justifyContent="flex-start"
                sx={{
                    m: 2
                }}
            >
                {selectedWorkPatternSet &&
                    <FormControl
                        size="small"
                    >
                        <InputLabel id="workpatternset">営業時間期間</InputLabel>
                        <Select
                            labelId="workpatternset"
                            value={selectedWorkPatternSet?.wrkptnset_id ? selectedWorkPatternSet?.wrkptnset_id : 0}
                            onChange={(e: SelectChangeEvent<number>) => {
                                if (e.target.value > 0) {
                                    let workPatternSet: WorkPatternSet | undefined = workPatternSetList.find(value => value.wrkptnset_id === e.target.value)
                                    setSelectedWorkPatternSet(workPatternSet)
                                } else {
                                    setSelectedWorkPatternSet(undefined)
                                }
                            }}
                            label="営業時間期間"
                        >
                            <MenuItem value={0} disabled />
                            {workPatternSetList.map((workPatternSet: WorkPatternSet) => (
                                <MenuItem value={workPatternSet.wrkptnset_id} key={workPatternSet.wrkptnset_id}>
                                    {workPatternSet.begin_date ? format(workPatternSet.begin_date, "yyyy/MM/dd") : "営業開始日"} ～ {workPatternSet.end_date && format(workPatternSet.end_date, "yyyy/MM/dd")}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                }
            </Stack>
            {workingTimeList && selectedWorkPatternSet &&
                <Box
                    sx={{
                        m: 2
                    }}
                >
                    <StoreWorkingTimeTable
                        wrkptnset_id={selectedWorkPatternSet.wrkptnset_id}
                        workingTimeList={workingTimeList}
                        requestReloadWorkPattern={() => {
                            if (selectedWorkPatternSet) {
                                loadStoreWorkingTimeList(selectedWorkPatternSet.wrkptnset_id)
                            }
                        }}
                    />
                </Box>
            }
            <Box
                sx={{
                    m: 2
                }}
            >
                <Typography variant="subtitle2">特定日の営業時間を設定します</Typography>
                <Divider />
                <Box
                    sx={{
                        maxWidth: "500px",
                        my: 1,
                        mx: "auto",
                    }}
                >
                    <Grid container
                        alignItems="center"
                        sx={{
                            my: 1,
                            px: 1,
                        }}
                    >
                        <Grid item
                            sx={{
                                marginLeft: 0,
                                marginRight: "auto",
                            }}
                        >{ showWrkPtcYear }年
                        {loadingWrkPtc &&
                            <CircularProgress size={20} />
                        }
                        </Grid>
                        <Grid item
                            sx={{
                                marginLeft: "auto",
                                marginRight: 0,
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                            >
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => {
                                        setShowWorkingParticularSettingDialog(true)
                                    }}
                                >新規登録</Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<NavigateBefore />}
                                    size="small"
                                    onClick={() => {
                                        setShowWrkPtcYear( prev => prev - 1)
                                    }}
                                >前年</Button>
                                <Button
                                    variant="outlined"
                                    endIcon={<NavigateNext />}
                                    size="small"
                                    onClick={() => {
                                        setShowWrkPtcYear( prev => prev + 1)
                                    }}
                                >翌年</Button>
                            </Stack>
                        </Grid>
                    </Grid>
                    <Divider />
                    <Stack
                        spacing={1}
                        sx={{
                            height: "500px",
                            overflowY: "auto",
                            px: 1
                        }}
                    >
                        {workingParticularSettingList?.map(workingParticularSetting => (
                            <Grid container key={workingParticularSetting.wrkptc_id}
                                alignItems="center"
                            >
                                <Grid item
                                    xs={5}
                                ><Typography>{ format( workingParticularSetting.wrkptc_date, "yyyy年MM月dd日" ) }</Typography></Grid>
                                <Grid item
                                    xs={6}
                                >
                                    { (workingParticularSetting.wrkptc_open && workingParticularSetting.wrkptc_close) ? (
                                        <Typography textAlign="center">{workingParticularSetting.wrkptc_open.toStringHM()} ～ {workingParticularSetting.wrkptc_close.toStringHM()}</Typography>
                                    ) : (
                                        <Typography textAlign="center">休館日</Typography>
                                    ) }
                                </Grid>
                                <Grid item
                                    xs={1}
                                >
                                    <IconButton
                                        aria-label="削除"
                                        onClick={() => {
                                            doDelete(workingParticularSetting.wrkptc_id)
                                        }}
                                    ><Delete /></IconButton>
                                </Grid>
                            </Grid>
                        ))}
                    </Stack>
                    <WorkingParticularSettingDialog
                        showDialog={showWorkingParticularSettingDialog}
                        storeId={storeObjectState.storeData?.str_id}
                        onClose={isEdit => {
                            if( isEdit && storeObjectState.storeData?.str_id ) {
                                loadStoreWorkingParticularSettingList(storeObjectState.storeData?.str_id, showWrkPtcYear);
                            }
                            setShowWorkingParticularSettingDialog(false)
                        }}
                    />
                </Box>
            </Box>
        </Paper>
    );
}

export default StoreDetailBusinessHours